import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  Snackbar,
} from '@mui/material';
import { 
  Visibility, 
  LocalShipping, 
  Person, 
  Phone, 
  Email, 
  LocationOn, 
  AccessTime,
  Restaurant,
  Euro,
  CheckCircle,
  Pending,
  Cancel
} from '@mui/icons-material';
import LiveDriverMap from '../../components/LiveDriverMap';

interface Order {
  id: string;
  customer: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  items: number;
  total: number;
  status: string;
  time: string;
  order_items?: OrderItem[];
  payment_method?: string;
  delivery_type?: string;
  notes?: string;
  rider_email?: string; // Driver assignment email
  assigned_driver_name?: string; // Driver name
  assigned_driver_phone?: string; // Driver phone
  created_by?: string; // Who created the order (staff/guest)
  source?: string; // Order source: 'pos', 'qr_order', etc.
  table_number?: number; // Table number for dine-in orders
}

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
}

interface Driver {
  id: string;
  name: string;
  phone: string;
  status: string;
}

const OrdersPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeliveryDialog, setOpenDeliveryDialog] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [driverLocation, setDriverLocation] = useState<{ latitude?: number; longitude?: number; timestamp?: string; is_online?: boolean } | null>(null);
  const [deliveryNotification, setDeliveryNotification] = useState<{ open: boolean; message: string; orderNumber?: string }>({ open: false, message: '' });
  const [previousOrders, setPreviousOrders] = useState<Map<string, string>>(new Map()); // Track previous statuses

  // Fetch real drivers from Firestore
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const { collection, getDocs, query, where } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        
        const driversRef = collection(db, 'drivers');
        // Only fetch active drivers
        const q = query(driversRef, where('is_active', '==', true));
        const driversSnapshot = await getDocs(q);
        
        const driversList: Driver[] = [];
        
        for (const doc of driversSnapshot.docs) {
          const data = doc.data();
          
          // Get real-time status from Realtime Database
          let driverStatus = data.status || 'offline';
          const email = data.email;
          
          if (email) {
            try {
              const safeEmail = email.replace(/@/g, '_').replace(/\./g, '_');
              const { auth } = await import('../../services/firebaseConfig');
              const user = auth.currentUser;
              
              if (user) {
                const token = await user.getIdToken();
                const url = `https://__SAHEEH_RTDB_UNSET__/drivers/${safeEmail}.json?auth=${token}`;
                const response = await fetch(url);
                
                if (response.ok) {
                  const realtimeData = await response.json();
                  if (realtimeData && realtimeData.status) {
                    driverStatus = realtimeData.status;
                  }
                }
              }
            } catch (e) {
              console.warn('Failed to fetch Realtime DB status:', e);
            }
          }
          
          driversList.push({
            id: doc.id,
            name: data.name || 'Unknown Driver',
            phone: data.phone || '',
            status: driverStatus,
          });
        }
        
        // Sort by name
        driversList.sort((a, b) => a.name.localeCompare(b.name));
        
        setDrivers(driversList);
        console.log(`✅ [ADMIN] Loaded ${driversList.length} drivers from Firestore`);
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching drivers:', error);
        setDrivers([]);
      }
    };
    
    fetchDrivers();
  }, []);

  // Fetch real orders from Firestore with real-time updates
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupOrdersListener = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe via Supabase. The legacy Firestore code below this point
        // is gated behind `if (false)` so it stays out of the build but the
        // surrounding `try/catch/return unsubscribe` shape compiles.
        const { subscribeAdminOrders } = await import('../../services/supabaseOrderService');
        unsubscribe = subscribeAdminOrders((incoming) => {
          // Run through the existing dedup/state-diff handling by mapping
          // into the page's local Order shape (already done by the service).
          setOrders(incoming as any);
          const currentStatuses = new Map<string, string>();
          incoming.forEach((o) => currentStatuses.set(o.id, o.status));
          setPreviousOrders(new Map(currentStatuses));
          setLoading(false);
        });
        return;

      } catch (err: any) {
        console.error('❌ [ADMIN ORDERS] Error fetching orders:', err);
        setError(err.message || 'Failed to fetch orders');
        setLoading(false);
      }
    };
    
    setupOrdersListener();
    
    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#A5D6A7';
      case 'confirmed': return '#4CAF50';
      case 'preparing': return '#81C784';
      case 'ready': return '#66BB6A';
      case 'on_delivery': return '#4CAF50';
      case 'delivered': return '#4CAF50';
      case 'cancelled': return '#d32f2f';
      default: return '#999999';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      default: return <Pending />;
    }
  };

  const formatCurrency = (amount: number) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  // Keep a live listener for driver location while dialog is open
  const driverLocationUnsubRef = React.useRef<null | (() => void)>(null);

  const handleViewOrder = async (order: Order) => {
    setSelectedOrder(order);
    setOpenViewDialog(true);
    setDriverLocation(null);
    
    // Fetch driver location if driver is assigned
    if (order.rider_email || order.assigned_driver_name) {
      try {
        const { collection, query, where, getDocs, doc, getDoc, onSnapshot } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        
        // First, find the driver document by email
        let driverId: string | null = null;
        
        if (order.rider_email) {
          const driversRef = collection(db, 'drivers');
          const q = query(driversRef, where('email', '==', order.rider_email));
          const driversSnapshot = await getDocs(q);
          
          if (!driversSnapshot.empty) {
            driverId = driversSnapshot.docs[0].id;
          }
        }
        
        // If we have driver ID, fetch location from driver_locations
        if (driverId) {
          const locationDocRef = doc(db, 'driver_locations', driverId);

          // Initial fetch
          const locationDocSnap = await getDoc(locationDocRef);
          if (locationDocSnap.exists()) {
            const locationData: any = locationDocSnap.data();
            setDriverLocation({
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              timestamp: locationData.timestamp?.toDate?.()?.toISOString() || locationData.timestamp,
              is_online: locationData.is_online || false,
            });
          }

          // Live updates while dialog is open
          driverLocationUnsubRef.current?.();
          driverLocationUnsubRef.current = onSnapshot(locationDocRef, (snap) => {
            if (snap.exists()) {
              const d: any = snap.data();
              setDriverLocation({
                latitude: d.latitude,
                longitude: d.longitude,
                timestamp: d.timestamp?.toDate?.()?.toISOString() || d.timestamp,
                is_online: d.is_online || false,
              });
            }
          });

          if (!locationDocSnap.exists()) {
            // Fallback: try to get location from drivers collection
            const driverDocRef = doc(db, 'drivers', driverId);
            const driverDocSnap = await getDoc(driverDocRef);
            
            if (driverDocSnap.exists()) {
              const driverData = driverDocSnap.data();
              if (driverData.current_latitude && driverData.current_longitude) {
                setDriverLocation({
                  latitude: driverData.current_latitude,
                  longitude: driverData.current_longitude,
                  is_online: driverData.is_available || false,
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching driver location:', error);
      }
    }
  };

  const closeAllDialogsWithCleanup = () => {
    driverLocationUnsubRef.current?.();
    driverLocationUnsubRef.current = null;
    setOpenViewDialog(false);
    setOpenDeliveryDialog(false);
  };

  const handleDeliveryManagement = (order: Order) => {
    setSelectedOrder(order);
    setSelectedDriver('');
    setDeliveryNotes('');
    setOpenDeliveryDialog(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedDriver || !selectedOrder) {
      console.warn('⚠️ [ADMIN] Missing driver or order selection');
      return;
    }

    try {
      console.log('🚗 [ADMIN] Assigning driver to order...');
      
      const selectedDriverData = drivers.find(d => d.id === selectedDriver);
      if (!selectedDriverData) {
        console.error('❌ [ADMIN] Driver not found');
        alert('Driver not found');
        return;
      }

      // Get driver details from Firestore
      const { doc, getDoc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      
      const driverDocRef = doc(db, 'drivers', selectedDriver);
      const driverDocSnap = await getDoc(driverDocRef);
      
      if (!driverDocSnap.exists()) {
        console.error('❌ [ADMIN] Driver document not found in Firestore');
        alert('Driver not found in database');
        return;
      }
      
      const driverData = driverDocSnap.data();
      const driverEmail = driverData.email || selectedDriverData.name;
      
      console.log('🚗 [ADMIN] Selected driver:', {
        id: selectedDriver,
        name: selectedDriverData.name,
        email: driverEmail,
      });

      // Update order in Firestore with assigned driver
      const orderRef = doc(db, 'orders', selectedOrder.id);
      
      const orderUpdateData = {
        // Driver assignment fields (for POS/Admin tracking)
        assigned_driver_id: selectedDriver,
        assigned_driver_name: selectedDriverData.name,
        assigned_driver_email: driverEmail,
        assigned_driver_phone: selectedDriverData.phone || driverData.phone || '',
        // Rider email (for rider app to find assigned orders) - CRITICAL
        rider_email: driverEmail,
        // Order status
        status: 'assigned',
        // Timestamps
        assigned_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      
      console.log('🚗 [ADMIN] Updating order in Firestore:', orderUpdateData);
      await updateDoc(orderRef, orderUpdateData);
      
      console.log('✅ [ADMIN] Driver assigned successfully!');
      alert(`Driver ${selectedDriverData.name} assigned to order successfully!`);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === selectedOrder.id 
          ? { 
              ...order, 
              status: 'assigned',
              rider_email: driverEmail,
              assigned_driver_name: selectedDriverData.name,
              assigned_driver_phone: selectedDriverData.phone || '',
            }
          : order
      ));
      
      // Update driver status to busy in Firestore
      await updateDoc(driverDocRef, {
        status: 'busy',
        is_available: false,
        updated_at: serverTimestamp(),
      });
      
      setOpenDeliveryDialog(false);
    } catch (error: any) {
      console.error('❌ [ADMIN] Error assigning driver:', error);
      alert(`Failed to assign driver: ${error.message || 'Please try again'}`);
    }
  };

  const handleCloseDialogs = () => {
    closeAllDialogsWithCleanup();
    setSelectedOrder(null);
    setSelectedDriver('');
    setDeliveryNotes('');
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 4 } }}>
        <Typography
          variant="h5"
          sx={{
            color: '#e0e0e0',
            fontWeight: 700,
            mb: { xs: 2, md: 4 },
            fontSize: { xs: '1.1rem', md: '1.5rem' },
            fontFamily: '"Poppins", sans-serif'
          }}
        >
        {t('orders.title')}
      </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#e0e0e0' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (<>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper sx={{
            backgroundColor: '#252838',
            border: '1px solid #3a3d52',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}>
            <TableContainer sx={{ 
              maxHeight: { xs: 'none', md: '70vh' }, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}>
        <Table stickyHeader>
          <TableHead>
                  <TableRow sx={{ backgroundColor: '#1e2139' }}>
                    <TableCell sx={{ 
                      color: '#e0e0e0', 
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.orderId')}
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#e0e0e0', 
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.customer')}
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#e0e0e0', 
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.items')}
                    </TableCell>
                    <TableCell sx={{
                      color: '#e0e0e0',
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.total')}
                    </TableCell>
                    <TableCell sx={{
                      color: '#e0e0e0',
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.status')}
                    </TableCell>
                    <TableCell sx={{
                      color: '#e0e0e0',
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.date')}
                    </TableCell>
                    <TableCell sx={{
                      color: '#e0e0e0',
                      fontWeight: 600,
                      fontFamily: '"Poppins", sans-serif',
                      borderBottom: '2px solid #3a3d52'
                    }}>
                      {t('orders.actions')}
                    </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        color: '#9ca3af',
                        fontFamily: '"Poppins", sans-serif'
                      }}>
                        {t('orders.noOrders')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow 
                        key={order.id} 
                        sx={{ 
                          '&:hover': { backgroundColor: '#1e2139' },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell sx={{
                          color: '#e0e0e0',
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {order.id}
                          {order.source === 'qr_order' && (
                            <Chip label="QR" size="small" sx={{ ml: 0.5, height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.12)', color: '#4CAF50' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {order.customer}
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {order.items} {t('orders.items')}
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.success.main, 
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {formatCurrency(order.total)}
                        </TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                  <Chip
                    label={order.status}
                    size="small"
                            icon={getStatusIcon(order.status)}
                    sx={{
                      backgroundColor: getStatusColor(order.status),
                              color: '#1a1d2e',
                              fontWeight: 600,
                      textTransform: 'capitalize',
                              fontFamily: '"Poppins", sans-serif'
                    }}
                  />
                </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {order.time}
                        </TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewOrder(order)}
                              sx={{ 
                                color: '#9ca3af',
                                '&:hover': { 
                                  color: '#e0e0e0',
                                  backgroundColor: '#1e2139'
                                }
                              }}
                              title={t('orders.viewOrderDetails')}
                            >
                    <Visibility />
                  </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeliveryManagement(order)}
                              disabled={order.status === 'delivered' || order.status === 'cancelled'}
                              sx={{ 
                                color: order.status === 'delivered' || order.status === 'cancelled' ? '#999999' : '#666666',
                                '&:hover': { 
                                  color: order.status === 'delivered' || order.status === 'cancelled' ? '#999999' : '#000000',
                                  backgroundColor: order.status === 'delivered' || order.status === 'cancelled' ? 'transparent' : '#1e2139'
                                }
                              }}
                              title={t('orders.manageDelivery')}
                            >
                    <LocalShipping />
                  </IconButton>
                          </Box>
                </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
            {orders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: '#9ca3af', fontFamily: '"Poppins", sans-serif' }}>
                  {t('orders.noOrders')}
                </Typography>
              </Box>
            ) : (
              orders.map((order) => (
                <Box key={order.id} sx={{
                  backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: '10px',
                  p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                  {/* Top: Order # + Status + Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', fontFamily: '"Poppins"' }}>#{order.id}</Typography>
                      {order.source === 'qr_order' && <Chip label="QR" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(0, 200, 150,0.15)', color: '#00C896' }} />}
                    </Box>
                    <Typography sx={{ color: '#00C896', fontWeight: 700, fontSize: '1rem', fontFamily: '"Poppins"' }}>€{order.total?.toFixed(2)}</Typography>
                  </Box>
                  {/* Customer + Items */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', fontFamily: '"Poppins"' }}>{order.customer}</Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.72rem', fontFamily: '"Poppins"' }}>{order.items} items</Typography>
                  </Box>
                  {/* Bottom: Status + Time + Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5, borderTop: '1px solid #2a2d3e' }}>
                    <Chip label={order.status} size="small" icon={getStatusIcon(order.status)} sx={{ bgcolor: getStatusColor(order.status), color: '#1a1d2e', fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
                    <Typography sx={{ color: '#6b7280', fontSize: '0.68rem', fontFamily: '"Poppins"' }}>{order.time}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleViewOrder(order)} sx={{ color: '#2196F3', width: 28, height: 28 }}><Visibility sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" onClick={() => handleDeliveryManagement(order)} disabled={order.status === 'delivered' || order.status === 'cancelled'} sx={{ color: '#FF9800', width: 28, height: 28 }}><LocalShipping sx={{ fontSize: 16 }} /></IconButton>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </>)}

        {/* View Order Details Dialog */}
        <Dialog 
          open={openViewDialog} 
          onClose={handleCloseDialogs}
          maxWidth="md" fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#e0e0e0',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            borderBottom: '1px solid #3a3d52'
          }}>
            {t('orders.orderDetails')} - {selectedOrder?.id}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 1.5, md: 3 } }}>
            {selectedOrder && (
              <Grid container spacing={3}>
                {/* Customer Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#e0e0e0', 
                    fontWeight: 600, 
                    mb: 2,
                    fontFamily: '"Poppins", sans-serif'
                  }}>
                    {t('orders.customerInfo')}
                  </Typography>
                  <List>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Person sx={{ color: '#e0e0e0' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={selectedOrder.customer}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#e0e0e0',
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 600
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Email sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={selectedOrder.customer_email}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Phone sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={selectedOrder.customer_phone}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LocationOn sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={selectedOrder.customer_address}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* Order Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#e0e0e0', 
                    fontWeight: 600, 
                    mb: 2,
                    fontFamily: '"Poppins", sans-serif'
                  }}>
                    {t('orders.orderInfo')}
                  </Typography>
                  <List>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <AccessTime sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${t('orders.orderDate')}: ${selectedOrder.time}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Restaurant sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${t('orders.type')}: ${selectedOrder.delivery_type}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Euro sx={{ color: '#9ca3af' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${t('orders.paymentMethod')}: ${selectedOrder.payment_method}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getStatusIcon(selectedOrder.status)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${t('orders.status')}: ${selectedOrder.status}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: '#e0e0e0',
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 600
                          }
                        }}
                      />
                    </ListItem>
                    {selectedOrder.created_by && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Person sx={{ color: '#9ca3af' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={`${t('orders.createdBy')}: ${selectedOrder.created_by}`}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              color: '#9ca3af',
                              fontFamily: '"Poppins", sans-serif'
                            }
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {/* Driver Assignment Information */}
                {(selectedOrder.rider_email || selectedOrder.assigned_driver_name) && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ 
                      color: '#e0e0e0', 
                      fontWeight: 600, 
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif'
                    }}>
                      {t('orders.driverAssignment')}
                    </Typography>
                    <List>
                      {selectedOrder.assigned_driver_name && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <LocalShipping sx={{ color: '#1976D2' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${t('orders.driver')}: ${selectedOrder.assigned_driver_name}`}
                            sx={{ 
                              '& .MuiListItemText-primary': { 
                                color: '#e0e0e0',
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 600
                              }
                            }}
                          />
                        </ListItem>
                      )}
                      {selectedOrder.rider_email && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Email sx={{ color: '#9ca3af' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${t('orders.driverEmail')}: ${selectedOrder.rider_email}`}
                            sx={{ 
                              '& .MuiListItemText-primary': { 
                                color: '#9ca3af',
                                fontFamily: '"Poppins", sans-serif'
                              }
                            }}
                          />
                        </ListItem>
                      )}
                      {selectedOrder.assigned_driver_phone && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Phone sx={{ color: '#9ca3af' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${t('orders.driverPhone')}: ${selectedOrder.assigned_driver_phone}`}
                            sx={{ 
                              '& .MuiListItemText-primary': { 
                                color: '#9ca3af',
                                fontFamily: '"Poppins", sans-serif'
                              }
                            }}
                          />
                        </ListItem>
                      )}
                      {driverLocation && driverLocation.latitude && driverLocation.longitude && (
                        <>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <LocationOn sx={{ color: '#1976D2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={t('orders.currentLocation')}
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: '#9ca3af', fontFamily: '"Poppins", sans-serif' }}>
                                    GPS: {driverLocation.latitude.toFixed(6)}, {driverLocation.longitude.toFixed(6)}
                                  </Typography>
                                  {driverLocation.timestamp && (
                                    <Typography variant="caption" sx={{ color: '#6b7280', fontFamily: '"Poppins", sans-serif' }}>
                                      Updated: {new Date(driverLocation.timestamp).toLocaleString()}
                                    </Typography>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<LocationOn />}
                                    sx={{ 
                                      mt: 1,
                                      fontSize: '0.75rem',
                                      fontFamily: '"Poppins", sans-serif'
                                    }}
                                    onClick={() => {
                                      const url = `https://www.google.com/maps?q=${driverLocation.latitude},${driverLocation.longitude}`;
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    {t('orders.viewOnMap')}
                                  </Button>
                                </Box>
                              }
                              sx={{ 
                                '& .MuiListItemText-primary': { 
                                  color: '#e0e0e0',
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: 600
                                }
                              }}
                            />
                          </ListItem>
                          {driverLocation.is_online !== undefined && (
                            <ListItem sx={{ px: 0 }}>
                              <ListItemIcon>
                                {driverLocation.is_online ? (
                                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                                ) : (
                                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#999999' }} />
                                )}
                              </ListItemIcon>
                              <ListItemText 
                                primary={driverLocation.is_online ? t('orders.driverOnline') : t('orders.driverOffline')}
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    color: driverLocation.is_online ? '#4CAF50' : '#999999',
                                    fontFamily: '"Poppins", sans-serif',
                                    fontWeight: 500
                                  }
                                }}
                              />
                            </ListItem>
                          )}

                          {/* Inline embedded live map directly under Driver Assignment */}
                          <ListItem sx={{ px: 0, mt: 1 }}>
                            <Box sx={{ width: '100%' }}>
                              <LiveDriverMap
                                driverEmail={selectedOrder.rider_email || ''}
                                driverName={selectedOrder.assigned_driver_name || 'Driver'}
                                deliveryAddress={selectedOrder.customer_address}
                                latitude={driverLocation.latitude}
                                longitude={driverLocation.longitude}
                              />
                            </Box>
                          </ListItem>
                        </>
                      )}
                    </List>
                  </Grid>
                )}

                {/* Order Items */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ 
                    color: '#e0e0e0', 
                    fontWeight: 600, 
                    mb: 2,
                    fontFamily: '"Poppins", sans-serif'
                  }}>
                    {t('orders.orderItems')}
                  </Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: '#e0e0e0' }}>{t('orders.item')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#e0e0e0' }}>{t('orders.quantity')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#e0e0e0' }}>{t('orders.price')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#e0e0e0' }}>{t('orders.total')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.order_items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell sx={{ color: '#e0e0e0' }}>{item.name}</TableCell>
                            <TableCell sx={{ color: '#9ca3af' }}>{item.quantity}</TableCell>
                            <TableCell sx={{ color: '#9ca3af' }}>{formatCurrency(item.price)}</TableCell>
                            <TableCell sx={{ color: theme.palette.success.main, fontWeight: 600 }}>{formatCurrency(item.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
                </Grid>

                {/* Order Notes */}
                {selectedOrder.notes && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ 
                      color: '#e0e0e0', 
                      fontWeight: 600, 
                      mb: 1,
                      fontFamily: '"Poppins", sans-serif'
                    }}>
                      {t('orders.specialNotes')}
                    </Typography>
                    <Typography sx={{ 
                      color: '#9ca3af',
                      fontFamily: '"Poppins", sans-serif'
                    }}>
                      {selectedOrder.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, md: 3 }, borderTop: '1px solid #3a3d52' }}>
            <Button 
              onClick={handleCloseDialogs}
              sx={{ 
                color: '#9ca3af',
                fontFamily: '"Poppins", sans-serif'
              }}
            >
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delivery Management Dialog */}
        <Dialog 
          open={openDeliveryDialog} 
          onClose={handleCloseDialogs}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#e0e0e0',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            borderBottom: '1px solid #3a3d52'
          }}>
            {t('orders.deliveryManagement')} - {selectedOrder?.id}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 1.5, md: 3 } }}>
            {selectedOrder && (
              <Box>
                <Typography variant="body1" sx={{ 
                  color: '#e0e0e0', 
                  mb: 3,
                  fontFamily: '"Poppins", sans-serif'
                }}>
                  {t('orders.assignDriverTo')} <strong>{selectedOrder.customer}</strong>
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ fontFamily: '"Poppins", sans-serif' }}>{t('orders.selectDriver')}</InputLabel>
                  <Select
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                    sx={{ fontFamily: '"Poppins", sans-serif' }}
                  >
                    {drivers.filter(driver => driver.status === 'available').map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.name} - {driver.phone}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={t('orders.deliveryNotes')}
                  multiline
                  rows={3}
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder={t('orders.deliveryInstructionsPlaceholder')}
                  sx={{ fontFamily: '"Poppins", sans-serif' }}
                />

                {selectedOrder.notes && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#1e2139', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ 
                      color: '#9ca3af',
                      fontFamily: '"Poppins", sans-serif'
                    }}>
                      <strong>{t('orders.customerNotes')}:</strong> {selectedOrder.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, md: 3 }, borderTop: '1px solid #3a3d52' }}>
            <Button 
              onClick={handleCloseDialogs}
              sx={{ 
                color: '#9ca3af',
                fontFamily: '"Poppins", sans-serif'
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAssignDriver}
              variant="contained"
              disabled={!selectedDriver}
              sx={{
                backgroundColor: '#00C896',
                color: '#1a1d2e',
                '&:hover': { backgroundColor: '#7bc912' },
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600
              }}
            >
              {t('orders.assignDriver')}
            </Button>
          </DialogActions>
        </Dialog>

      {/* Delivery Completion Notification */}
      <Snackbar
        open={deliveryNotification.open}
        autoHideDuration={6000}
        onClose={() => setDeliveryNotification({ ...deliveryNotification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setDeliveryNotification({ ...deliveryNotification, open: false })}
          severity="success"
          icon={<CheckCircle />}
          sx={{
            width: '100%',
            minWidth: 400,
            backgroundColor: '#4CAF50',
            color: '#1a1d2e',
            '& .MuiAlert-icon': {
              color: '#1a1d2e',
            },
            '& .MuiAlert-message': {
              fontWeight: 600,
            },
          }}
        >
          {deliveryNotification.message}
        </Alert>
      </Snackbar>
    </Box>
    </AdminLayout>
  );
};

export default OrdersPage;